/**
 * Where the back arrow goes: up the tree, not back through the bounces.
 *
 * WHAT WAS WRONG. The arrow undid the last navigation. Somebody who had gone
 * from a group to a member, back to the group, to the member again, then
 * pressed back, walked their own footsteps in reverse instead of returning to
 * the group. Reported by Maude on 22 September 2026: "si j'ai fait des allers
 * retours sur des pages, si je clique retour ça me fait faire mes allers
 * retours au lieu de faire l'arbre".
 *
 * WHY IT IS COMPUTED RATHER THAN DECLARED. The shared header already accepts a
 * parent, `pathBack`, and eleven screens out of fifty-nine pass one. The other
 * forty-eight fall through to the history. Declaring forty-eight parents by
 * hand would fix today and nothing else: the screen written next month forgets
 * it again, in silence, and the bug comes back one page at a time.
 *
 * The URL already is the tree. `/home/member/:id/abonnements` sits under
 * `/home/member/:id`, which sits under `/home`. So the parent is read from the
 * address, and a screen only declares one when the address lies.
 *
 * THE ONE RULE THAT NEEDS CARE. Not every level of a URL is a screen.
 * `/home/detail-activity/:id/payment/register` has no `payment` page above it;
 * the real parent is `/home/detail-activity/:id`. So this climbs until it
 * finds a level the application actually declares, and gives up rather than
 * inventing one.
 */

/** A segment that identifies a thing rather than naming a screen. */
const looksLikeAnId = (segment: string): boolean =>
  /^[0-9a-f]{24}$/i.test(segment) ||
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
  /^\d+$/.test(segment);

/** The address without what comes after it: `?from=x`, `#top`. */
export const pathOf = (url: string): string => url.split('?')[0].split('#')[0];

export const segmentsOf = (url: string): string[] => pathOf(url).split('/').filter(Boolean);

/**
 * Every level above this one, nearest first.
 *
 * An id is dropped with the name it identifies: above
 * `/home/member/abc/abonnements` comes `/home/member/abc`, then `/home`, never
 * `/home/member`, which is not a screen and never was.
 */
export const ancestorsOf = (url: string): string[] => {
  const segments = segmentsOf(url);
  const climbed: string[] = [];

  for (let end = segments.length - 1; end > 0; end -= 1) {
    const candidate = segments.slice(0, end);
    if (looksLikeAnId(candidate[candidate.length - 1]) && end > 1) {
      // An id on its own is not a page. Keep it with the name above it and let
      // the next turn of the loop drop both.
      climbed.push(`/${candidate.join('/')}`);
      continue;
    }
    climbed.push(`/${candidate.join('/')}`);
  }

  return climbed;
};

/**
 * The screen the back arrow should open, or nothing when none is known.
 *
 * `isAScreen` is asked rather than assumed: only the application knows which
 * addresses it serves, and a back arrow that navigates to a route nobody
 * declared lands on a blank page, which is worse than the bug it replaces.
 */
export const parentOf = (url: string, isAScreen: (candidate: string) => boolean): string | undefined =>
  ancestorsOf(url).find((candidate) => isAScreen(candidate));
